import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { timeEntriesAPI, projectsAPI, usersAPI, importAPI } from '../services/api';
import useAuthStore from '../stores/authStore';
import { ChevronLeft, ChevronRight, Plus, AlertCircle, X, Clock, CheckCircle2, Upload, Download, MessageSquare, Eye } from 'lucide-react';
import { toLocalDateString, isDateEditable, getDaysInMonth } from '../utils/dateUtils';

const SPECIAL_ACTIVITY_OPTIONS = [
  { value: 'ADMIN_WORK', label: 'Admin Work' },
  { value: 'BENCH', label: 'Bench' },
  { value: 'HOLIDAY', label: 'Holiday / PTO' },
  { value: 'IDLE_TIME', label: 'Idle Time' },
  { value: 'MEETING_TIME', label: 'Meeting Time' },
  { value: 'TRAINING_TIME', label: 'Training Time' },
];

const emptyFormData = {
  selection: '',
  project_id: '',
  activity_type: '',
  hours: '',
  notes: '',
  user_id: '',
  late_entry_reason: '',
};



const Timesheets = () => {
  const location = useLocation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [formData, setFormData] = useState(emptyFormData);
  const { user: currentUser, isAdmin: checkIsAdmin } = useAuthStore();
  const isAdmin = checkIsAdmin();
  const [users, setUsers] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const csvInputRef = useRef(null);
  
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [selectedEntryForComments, setSelectedEntryForComments] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [lastAutosave, setLastAutosave] = useState(null);
  const [activeDraftId, setActiveDraftId] = useState(null);
  const autosaveTimerRef = useRef(null);

  useEffect(() => {
    fetchProjects();
    
    // Handle date or entry_id from URL (e.g. from Notifications)
    const params = new URLSearchParams(location.search);
    const urlDate = params.get('date');
    const urlEntryId = params.get('entry_id');
    
    if (urlDate) {
      setSelectedDate(urlDate);
      setShowModal(true);
      const d = new Date(urlDate);
      if (d.getMonth() !== currentDate.getMonth() || d.getFullYear() !== currentDate.getFullYear()) {
        setCurrentDate(d);
      }
    } else if (urlEntryId) {
      // If we have an entry_id, we need to find its date first
      // This is handled in fetchEntries if needed, but for now we assume entry is in current view or fetched soon
    }

    if (isAdmin) {
      const fetchUsers = async () => {
        try {
          const resp = await usersAPI.list({ page: 1, size: 100 });
          setUsers(resp?.data?.data?.users || resp?.data?.users || []);
        } catch (error) {
          console.error(error);
        }
      };
      fetchUsers();
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchEntries();
  }, [currentDate]);

  // Autosave logic
  useEffect(() => {
    const isHoliday = formData.activity_type === 'HOLIDAY';
    if (showModal && !editingEntry && formData.selection && (isHoliday || formData.hours)) {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      
      autosaveTimerRef.current = setTimeout(async () => {
        setIsSaving(true);
        try {
          const hoursValue = isHoliday ? 0 : parseFloat(formData.hours);
          if (isHoliday || (!Number.isNaN(hoursValue) && hoursValue >= 0.5 && hoursValue <= 24)) {
            // Only autosave if valid hours
            const payload = {
              work_date: selectedDate,
              hours: hoursValue,
              notes: formData.notes || '',
              status: 'DRAFT'
            };
            if (formData.project_id) payload.project_id = parseInt(formData.project_id, 10);
            if (formData.activity_type) payload.activity_type = formData.activity_type;
            
            if (activeDraftId) {
              await timeEntriesAPI.update(activeDraftId, payload);
            } else {
              const response = await timeEntriesAPI.create(payload);
              setActiveDraftId(response?.data?.data?.id || response?.data?.id);
            }
            setLastAutosave(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
            // fetchEntries(); removed to allow silent background saving without UI interruption
          }
        } catch (e) {
          console.error("Autosave failed", e);
        } finally {
          setIsSaving(false);
        }
      }, 3000); // 3 second debounce
    }
    
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [formData, showModal, editingEntry]);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.list({ assigned_only: true, status_filter: true });
      let allProjects = response?.data?.data?.projects || [];
      if (!isAdmin) {
        allProjects = allProjects.filter(p => {
          const isMember = (p.members || []).some(m => m.user_id === currentUser?.id);
          const isProjMgr = (p.manager_ids || []).includes(currentUser?.id) || (p.managers || []).some(m => m.id === currentUser?.id) || p.manager_id === currentUser?.id;
          return isMember || isProjMgr;
        });
      }
      setProjects(allProjects);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const response = await timeEntriesAPI.list({
        start_date: toLocalDateString(new Date(year, month, 1)),
        end_date: toLocalDateString(new Date(year, month + 1, 0)),
      });
      const fetchedEntries = response?.data?.data?.entries || [];
      setEntries(fetchedEntries);
      
      // Deep link support: if urlEntryId exists, open that entry
      const params = new URLSearchParams(location.search);
      const urlEntryId = params.get('entry_id');
      if (urlEntryId && fetchedEntries.length > 0) {
        const entry = fetchedEntries.find(e => e.id.toString() === urlEntryId);
        if (entry) {
          setSelectedDate(entry.work_date);
          if (entry.status === 'DRAFT') {
            handleEditEntry(entry);
          } else {
            handleViewEntry(entry);
          }
          setShowModal(true);
          // Clean up URL to prevent re-opening on every refresh
          window.history.replaceState({}, '', window.location.pathname);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setShowModal(false);
    setEditingEntry(null);
    setIsViewOnly(false);
    setFormData(emptyFormData);
    setActiveDraftId(null);
    setLastAutosave(null);
  };

  const downloadSampleCsv = () => {
    const today = toLocalDateString(new Date());
    const sample = [
      'date,project_name,activity_type,hours,notes',
      `${today},My Project Name,,8,Worked on feature X`,
      `${today},,TRAINING_TIME,2,Team training session`,
    ].join('\n');
    const blob = new Blob([sample], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'timesheets_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    setIsImporting(true);
    try {
      const response = await importAPI.timeEntries(file);
      const payload = response?.data?.data || {};
      const errs = response?.data?.errors || [];
      let msg = `✅ Import complete!\n• Created: ${payload.success_count ?? 0}\n• Failed: ${payload.failure_count ?? 0}`;
      if (errs.length > 0) {
        msg += `\n\nErrors:\n${errs.slice(0, 10).join('\n')}`;
        if (errs.length > 10) msg += `\n...and ${errs.length - 10} more.`;
      }
      alert(msg);
      fetchEntries();
    } catch (error) {
      const detail = error.response?.data?.detail || error.response?.data?.message || error.message;
      alert(`Import failed: ${detail}`);
    } finally {
      setIsImporting(false);
    }
  };



  const handleSelectionChange = (value) => {
    if (!value) {
      setFormData(prev => ({ ...prev, selection: '', project_id: '', activity_type: '' }));
      return;
    }

    if (value.startsWith('project:')) {
      setFormData(prev => ({
        ...prev,
        selection: value,
        project_id: value.replace('project:', ''),
        activity_type: '',
      }));
      return;
    }

    const activityType = value.replace('activity:', '');
    setFormData(prev => ({
      ...prev,
      selection: value,
      project_id: '',
      activity_type: activityType,
      // Clear hours & notes when Holiday is selected — not required
      ...(activityType === 'HOLIDAY' ? { hours: '', notes: '' } : {}),
    }));
  };

  const openComments = (entry) => {
    setSelectedEntryForComments(entry);
    // Load draft comment if exists
    const draft = localStorage.getItem(`comment_draft_${entry.id}`);
    setNewComment(draft || '');
    setShowCommentsModal(true);
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedEntryForComments) return;
    try {
      await timeEntriesAPI.addComment(selectedEntryForComments.id, { comment: newComment.trim() });
      setNewComment('');
      localStorage.removeItem(`comment_draft_${selectedEntryForComments.id}`);
      // Refresh entries to immediately see the new comment
      await fetchEntries();
      
      // Update the local modal's selected entry with the newly fetched instance so UI updates
      const updatedList = await timeEntriesAPI.list({
         start_date: toLocalDateString(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)),
         end_date: toLocalDateString(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)),
      });
      const match = updatedList?.data?.data?.entries?.find(e => e.id === selectedEntryForComments.id);
      if (match) setSelectedEntryForComments(match);
      
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    }
  };

  const handleSubmit = async (e, forcedStatus = null) => {
    if (e) e.preventDefault();

    const isHoliday = formData.activity_type === 'HOLIDAY';
    let hoursValue = 0;
    if (!isHoliday) {
      hoursValue = parseFloat(formData.hours);
      if (Number.isNaN(hoursValue) || hoursValue < 0.5 || hoursValue > 24 || (hoursValue % 0.5 !== 0)) {
        alert('Hours must be between 0.5 and 24, in 0.5 hour increments (e.g., 0.5, 1.0, 1.5).');
        return;
      }
    }

    try {
      if (editingEntry || activeDraftId) {
        const id = editingEntry ? editingEntry.id : activeDraftId;
        const updateData = { hours: isHoliday ? 0 : hoursValue, status: forcedStatus || 'PENDING' };
        if (formData.notes) updateData.notes = formData.notes;
        else if (isHoliday) updateData.notes = 'Holiday / PTO';
        if (formData.project_id) updateData.project_id = parseInt(formData.project_id, 10);
        if (formData.activity_type) updateData.activity_type = formData.activity_type;

        await timeEntriesAPI.update(id, updateData);
        if (!forcedStatus || forcedStatus === 'PENDING') {
           resetModal();
           let msg = 'Entry submitted successfully!';
           if (editingEntry) {
             if (editingEntry.status === 'REJECTED') msg = 'Entry resubmitted successfully!';
             else if (editingEntry.status === 'DRAFT') msg = 'Entry updated and submitted successfully!';
             else msg = 'Entry updated successfully!';
           }
           alert(msg);
        }
        fetchEntries();
        return;
      }

      const payload = {
        work_date: selectedDate,
        hours: isHoliday ? 0 : hoursValue,
        notes: isHoliday ? (formData.notes || 'Holiday / PTO') : formData.notes,
        late_entry_reason: formData.late_entry_reason,
        status: forcedStatus || 'PENDING',
      };

      if (formData.user_id) {
        payload.user_id = parseInt(formData.user_id, 10);
      }
      if (formData.project_id) {
        payload.project_id = parseInt(formData.project_id, 10);
      }
      if (formData.activity_type) {
        payload.activity_type = formData.activity_type;
      }

      const response = await timeEntriesAPI.create(payload);
      if (forcedStatus === 'DRAFT') {
          setActiveDraftId(response?.data?.data?.id || response?.data?.id);
      } else {
          resetModal();
          alert('Entry submitted successfully!');
      }
      fetchEntries();
    } catch (error) {
      console.error('Time entry save failed:', error);
      const detail = error.response?.data?.detail;
      const message = error.response?.data?.message;
      alert(detail || message || error.message || 'Save failed. Please check your connection and data.');
    } finally {
      setLoading(false);
    }
  };



  const getDailyTotal = (date) => (
    entries
      .filter(entry => entry.work_date === date && (entry.activity_type || '').toUpperCase() !== 'HOLIDAY')
      .reduce((sum, entry) => sum + (parseFloat(entry.hours) || 0), 0)
  );

  const getEntriesForDate = (date) => entries.filter(entry => entry.work_date === date);

  const getRejectedEntriesForDate = (date) => (
    entries.filter(entry => entry.work_date === date && entry.status === 'REJECTED')
  );

  const handleDateClick = (dateStr) => {
    setSelectedDate(dateStr);
    setEditingEntry(null);
    setFormData(emptyFormData);
    setShowModal(true);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setIsViewOnly(false);
    if (entry.status === 'DRAFT') {
      setActiveDraftId(entry.id);
    }
    setFormData({
      selection: entry.activity_type ? `activity:${entry.activity_type}` : `project:${entry.project_id}`,
      project_id: entry.project_id ? entry.project_id.toString() : '',
      activity_type: entry.activity_type || '',
      hours: entry.hours ? entry.hours.toString() : '',
      notes: entry.notes || '',
      user_id: '',
      late_entry_reason: entry.late_entry_reason || '',
    });
  };

  const handleViewEntry = (entry) => {
    setEditingEntry(entry);
    setIsViewOnly(true);
    setFormData({
      selection: entry.activity_type ? `activity:${entry.activity_type}` : `project:${entry.project_id}`,
      project_id: entry.project_id ? entry.project_id.toString() : '',
      activity_type: entry.activity_type || '',
      hours: entry.hours ? entry.hours.toString() : '',
      notes: entry.notes || '',
      user_id: '',
      late_entry_reason: entry.late_entry_reason || '',
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <header className="header">
        <div className="header-title">
          <h2>Timesheets</h2>
          <p>{currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}</p>
        </div>
        <div className="header-actions">
          <div className="flex gap-2">
            <button className="btn btn-secondary btn-icon" onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}>
              <ChevronLeft size={20} />
            </button>
            <button className="btn btn-secondary btn-icon" onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}>
              <ChevronRight size={20} />
            </button>
            <button className="btn btn-primary" onClick={() => setCurrentDate(new Date())}>Today</button>
            <button
              className="btn btn-secondary"
              onClick={downloadSampleCsv}
              title="Download sample CSV template"
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Download size={16} /> Sample CSV
            </button>
            <input
              type="file"
              accept=".csv"
              ref={csvInputRef}
              style={{ display: 'none' }}
              onChange={handleCsvUpload}
            />
            <button
              className="btn btn-primary"
              onClick={() => csvInputRef.current?.click()}
              disabled={isImporting}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Upload size={16} /> {isImporting ? 'Importing…' : 'Import CSV'}
            </button>
          </div>
        </div>
      </header>

      <div className="page-content">
        <div className="calendar-card">
          <div className="calendar-week-days">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className="week-day">{day}</div>)}
          </div>
          <div className="calendar-grid">
            {getDaysInMonth(currentDate).map((day, idx) => {
              const dateStr = day ? toLocalDateString(day) : null;
              const active = isDateEditable(day, isAdmin, currentUser);
              const dailyTotal = day ? getDailyTotal(dateStr) : 0;
              const hasRejected = day ? getRejectedEntriesForDate(dateStr).length > 0 : false;

              return (
                <div
                  key={idx}
                  className={`calendar-day ${!day ? 'empty' : ''} ${!active && day ? 'disabled' : ''} ${dailyTotal > 0 ? 'has-entries' : ''} ${hasRejected ? 'has-rejected' : ''}`}
                  onClick={() => active && handleDateClick(dateStr)}
                >
                  {day && (
                    <>
                      <div className="day-header">
                        <span className="day-number">{day.getDate()}</span>
                        {active && <Plus className="quick-add-icon" size={14} />}
                      </div>
                      <div className="day-content">
                        {dailyTotal > 0 && (
                          <div className={`total-pill ${dailyTotal >= 8 ? 'complete' : 'incomplete'}`}>
                            {dailyTotal.toFixed(1)}h
                          </div>
                        )}
                        {entries.some(e => e.work_date === dateStr && (e.activity_type || '').toUpperCase() === 'HOLIDAY') && (
                          <div style={{
                            fontSize: '10px',
                            fontWeight: 600,
                            padding: '2px 5px',
                            borderRadius: '4px',
                            background: 'rgba(59, 130, 246, 0.15)',
                            color: '#2563eb',
                            display: 'inline-block',
                            marginTop: '2px'
                          }}>
                            Holiday / PTO
                          </div>
                        )}
                        <div className="indicators-row">
                          {hasRejected && <div className="rejected-dot" title="Has rejected entries" />}
                          {entries.some(e => e.work_date === dateStr && e.status === 'DRAFT') && (
                            <div className="draft-indicator" title="Has draft entries" />
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="premium-drawer-overlay" onClick={resetModal}>
          <div className="premium-drawer" onClick={e => e.stopPropagation()}>
            <div className="drawer-header">
              <h3>{isViewOnly ? 'View Entry' : (editingEntry ? 'Edit Entry' : 'Log Time')}</h3>
              <button className="btn btn-icon" onClick={resetModal}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <div className="drawer-body">
                <div style={{ display: 'flex', background: '#f8fafc', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px', marginBottom: '24px', alignItems: 'center' }}>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(13, 148, 136, 0.1)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Clock size={18} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>Daily Total</span>
                      <span style={{ fontSize: '16px', fontWeight: 700 }}>{getDailyTotal(selectedDate).toFixed(1)}h</span>
                    </div>
                  </div>
                  <div style={{ width: '1px', height: '30px', background: 'var(--border)', margin: '0 20px' }}></div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: getDailyTotal(selectedDate) >= 8 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)', color: getDailyTotal(selectedDate) >= 8 ? 'var(--success)' : 'var(--warning)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CheckCircle2 size={18} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>Goal (8h)</span>
                      <span style={{ fontSize: '16px', fontWeight: 700, color: getDailyTotal(selectedDate) >= 8 ? 'var(--success)' : 'var(--warning)' }}>
                        {(getDailyTotal(selectedDate) / 8 * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                </div>

                {getRejectedEntriesForDate(selectedDate).length > 0 && !editingEntry && (
                  <div style={{ background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#c53030', marginBottom: '12px', fontWeight: 600 }}>
                      <AlertCircle size={18} />
                      <span>Action Required: Revisions Needed</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {getRejectedEntriesForDate(selectedDate).map(entry => (
                        <div key={entry.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', padding: '8px 12px', borderRadius: '8px', border: '1px solid #feb2b2' }}>
                          <span style={{ fontSize: '13px', fontWeight: 500 }}>{entry.project_name}</span>
                          <button type="button" className="btn btn-sm btn-primary" onClick={() => handleEditEntry(entry)}>Fix Now</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {editingEntry && editingEntry.status === 'REJECTED' && editingEntry.rejection_reason && (
                  <div style={{ marginBottom: '20px', padding: '12px 16px', background: '#fff5f5', borderLeft: '4px solid #feb2b2', borderRadius: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <AlertCircle size={16} color="#c53030" />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#c53030' }}>Rejection Reason</span>
                    </div>
                    <p style={{ fontSize: '13px', color: '#742a2a', margin: 0 }}>{editingEntry.rejection_reason}</p>
                  </div>
                )}

                <div className="drawer-form-sections">
                  {isAdmin && !editingEntry && (
                    <div className="form-group">
                      <label className="form-label">On Behalf Of</label>
                      <select className="form-select" value={formData.user_id} onChange={e => setFormData(prev => ({ ...prev, user_id: e.target.value }))} style={{ background: '#f1f5f9' }}>
                        <option value="">Myself</option>
                        {users.map(user => <option key={user.id} value={user.id}>{user.name}</option>)}
                      </select>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '16px' }}>
                    <div className="form-group" style={{ flex: 2 }}>
                      <label className="form-label">Project / Activity</label>
                      <select
                        className="form-select"
                        value={formData.selection}
                        onChange={e => handleSelectionChange(e.target.value)}
                        disabled={editingEntry || isViewOnly}
                        required
                      >
                        <option value="">Choose project or activity...</option>
                        <optgroup label="Projects">
                          {projects.map(project => (
                            <option key={`project-${project.id}`} value={`project:${project.id}`}>
                              {project.name}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="Activities">
                          {SPECIAL_ACTIVITY_OPTIONS.map(option => (
                            <option key={`activity-${option.value}`} value={`activity:${option.value}`}>
                              {option.label}
                            </option>
                          ))}
                        </optgroup>
                      </select>
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label className="form-label">Hours</label>
                      <input 
                        type="number" 
                        step="0.5" 
                        min="0.5" 
                        max="24" 
                        className="form-input" 
                        placeholder={formData.activity_type === 'HOLIDAY' ? 'N/A' : '0.0'}
                        value={formData.hours} 
                        onChange={e => setFormData(prev => ({ ...prev, hours: e.target.value }))} 
                        onKeyDown={(e) => {
                          if (['-', '+', 'e', 'E'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        disabled={isViewOnly || formData.activity_type === 'HOLIDAY'}
                        required={formData.activity_type !== 'HOLIDAY'}
                        style={formData.activity_type === 'HOLIDAY' ? { background: '#f1f5f9', cursor: 'not-allowed', opacity: 0.6 } : {}}
                      />
                    </div>
                  </div>

                  {formData.activity_type === 'HOLIDAY' && (
                    <div style={{
                      background: 'rgba(59, 130, 246, 0.06)',
                      border: '1px solid rgba(59, 130, 246, 0.25)',
                      borderRadius: '10px',
                      padding: '10px 14px',
                      fontSize: '13px',
                      color: '#1d4ed8',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                    }}>
                      <span style={{ fontSize: '16px' }}>🏖️</span>
                      <span>Holiday / PTO entries don't require hours or activity notes.</span>
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">Activity Notes</label>
                    <textarea
                      className="form-input"
                      placeholder={formData.activity_type === 'HOLIDAY' ? 'Not required for Holiday / PTO' : (editingEntry ? 'Describe your corrections...' : 'What did you work on today?')}
                      rows="3"
                      value={formData.notes}
                      onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      disabled={isViewOnly || formData.activity_type === 'HOLIDAY'}
                      maxLength={1000}
                      required={formData.activity_type !== 'HOLIDAY'}
                      style={{ resize: 'none', ...(formData.activity_type === 'HOLIDAY' ? { background: '#f1f5f9', cursor: 'not-allowed', opacity: 0.6 } : {}) }}
                    />
                    <div className="flex justify-between" style={{ fontSize: '11px', marginTop: '4px', color: '#64748b' }}>
                      <span>{formData.activity_type === 'HOLIDAY' ? 'Not required' : 'Required'}</span>
                      <span>{formData.notes.length}/1000 characters</span>
                    </div>
                  </div>

                  {/* Show late reason if date is locked OR if admin is logging for someone else */}
                  {((!isDateEditable(selectedDate, isAdmin, currentUser) || (new Date(selectedDate) < new Date(new Date().setDate(new Date().getDate() - new Date().getDay() + (new Date().getDay() === 0 ? -6 : 1))).setHours(0,0,0,0))) || (isAdmin && formData.user_id)) && (
                    <div className="form-group" style={{ marginTop: '16px' }}>
                      <label className="form-label" style={{ color: 'var(--warning)', fontWeight: 700 }}>
                        {isAdmin ? 'Administrative Note / Reason' : 'Late Entry Reason'}
                      </label>
                      <textarea
                        className="form-input"
                        placeholder="Please provide a reason for this administrative or late entry..."
                        rows="2"
                        value={formData.late_entry_reason}
                        onChange={e => setFormData(prev => ({ ...prev, late_entry_reason: e.target.value }))}
                        disabled={isViewOnly}
                        required
                        style={{ resize: 'none', border: '1px solid var(--warning)', background: 'rgba(245, 158, 11, 0.02)' }}
                      />
                    </div>
                  )}
                </div>

                {!editingEntry && getEntriesForDate(selectedDate).filter(entry => entry.status !== 'REJECTED').length > 0 && (
                  <div style={{ marginTop: '32px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '12px' }}>
                      Logged Today ({getDailyTotal(selectedDate).toFixed(1)}h total)
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {getEntriesForDate(selectedDate).filter(entry => entry.status !== 'REJECTED').map(entry => (
                        <div 
                          key={entry.id} 
                          onClick={() => entry.status === 'DRAFT' ? handleEditEntry(entry) : handleViewEntry(entry)}
                          className="clickable-entry"
                          style={{ 
                            background: entry.status === 'DRAFT' ? 'rgba(59, 130, 246, 0.05)' : (editingEntry?.id === entry.id ? 'rgba(13, 148, 136, 0.05)' : 'white'), 
                            padding: '12px 16px', 
                            borderRadius: '12px', 
                            fontSize: '13px', 
                            border: editingEntry?.id === entry.id ? '2px solid var(--primary)' : (entry.status === 'DRAFT' ? '1px dashed var(--primary)' : '1px solid var(--border)'), 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            opacity: (editingEntry && editingEntry.id !== entry.id) ? 0.7 : 1
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{entry.project_name}</span>
                              {entry.status === 'DRAFT' && <span style={{ fontSize: '10px', background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', padding: '2px 6px', borderRadius: '4px', fontWeight: 700 }}>DRAFT</span>}
                              {entry.status === 'APPROVED' && <span style={{ fontSize: '10px', background: 'rgba(16, 185, 129, 0.1)', color: 'var(--success)', padding: '2px 6px', borderRadius: '4px', fontWeight: 700 }}>APPROVED</span>}
                            </div>
                            {entry.activity_type && <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{entry.activity_type.replace('_', ' ')}</span>}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ color: 'var(--primary)', fontWeight: 700, background: 'rgba(13, 148, 136, 0.1)', padding: '4px 10px', borderRadius: '20px' }}>{entry.hours}h</span>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              {entry.status === 'DRAFT' && (
                                <button type="button" onClick={() => handleEditEntry(entry)} className="btn-icon" title="Resume/Edit Draft" style={{ color: 'var(--primary)' }}>
                                  <Clock size={18} />
                                </button>
                              )}
                              {entry.status !== 'DRAFT' && (
                                <button type="button" onClick={() => handleViewEntry(entry)} className="btn-icon" title="View Entry" style={{ color: 'var(--text-secondary)' }}>
                                  <Eye size={18} />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openComments(entry); }}
                                className="btn-icon"
                                style={{ color: (entry.comments && entry.comments.length > 0) ? 'var(--primary)' : 'var(--text-secondary)', background: (entry.comments && entry.comments.length > 0) ? 'rgba(13, 148, 136, 0.1)' : 'transparent', position: 'relative' }}
                                title="View/Add Comments"
                              >
                                <MessageSquare size={18} />
                                {entry.comments && entry.comments.length > 0 && (
                                  <span style={{ position: 'absolute', top: -5, right: -5, background: 'var(--primary)', color: 'white', fontSize: '10px', borderRadius: '10px', minWidth: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                                    {entry.comments.length}
                                  </span>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="drawer-footer" style={{ justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {isSaving && <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Saving...</span>}
                  {lastAutosave && !isSaving && <span style={{ fontSize: '12px', color: 'var(--success)' }}>Autosaved {lastAutosave}</span>}
                </div>
                <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
                  {editingEntry && ['DRAFT', 'PENDING', 'REJECTED'].includes(editingEntry.status) && (
                    <button 
                      type="button" 
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete this entry?')) {
                          try {
                            await timeEntriesAPI.delete(editingEntry.id);
                            resetModal();
                            fetchEntries();
                          } catch (error) {
                            console.error('Error deleting entry:', error);
                            alert('Failed to delete entry');
                          }
                        }
                      }}
                      className="btn"
                      style={{ 
                        marginRight: 'auto',
                        backgroundColor: 'transparent',
                        color: '#ef4444',
                        border: '1px solid #ef4444'
                      }}
                    >
                      Delete
                    </button>
                  )}
                  <button type="button" onClick={resetModal} className="btn btn-secondary" style={{ marginLeft: !editingEntry ? 'auto' : '0' }}>
                    Cancel
                  </button>
                  {!editingEntry && (
                    <button 
                      type="button" 
                      onClick={() => handleSubmit(null, 'DRAFT')} 
                      className="btn btn-secondary"
                      style={{ border: '1px solid var(--primary)', color: 'var(--primary)' }}
                    >
                      Save as Draft
                    </button>
                  )}
                  {!isViewOnly && (
                    <button type="submit" className="btn btn-primary">
                      {editingEntry ? (
                        editingEntry.status === 'REJECTED' ? 'Update and Resubmit' : 'Update and Submit'
                      ) : 'Submit Entry'}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Comments Drawer */}
      {showCommentsModal && selectedEntryForComments && (
        <div className="premium-drawer-overlay" onClick={() => setShowCommentsModal(false)} style={{ zIndex: 1100 }}>
          <div className="premium-drawer" style={{ maxWidth: '400px', zIndex: 1101 }} onClick={e => e.stopPropagation()}>
            <div className="drawer-header">
              <h3>Comments</h3>
              <button className="btn-icon" onClick={() => setShowCommentsModal(false)}><X size={20} /></button>
            </div>
            
            <div style={{ padding: '16px 24px', background: '#f8fafc', borderBottom: '1px solid var(--border)' }}>
               <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Entry</div>
               <div style={{ fontWeight: 600 }}>{selectedEntryForComments.project_name} - {selectedEntryForComments.hours}h</div>
            </div>

            <div className="drawer-body" style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto', background: '#f1f5f9', padding: '24px' }}>
              {(!selectedEntryForComments.comments || selectedEntryForComments.comments.length === 0) ? (
                <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '40px 0', fontSize: '14px', fontStyle: 'italic' }}>
                  No comments on this entry yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {selectedEntryForComments.comments.map(c => {
                    const isOwn = c.user_name === JSON.parse(localStorage.getItem('user') || '{}').name;
                    return (
                      <div key={c.id} style={{ alignSelf: isOwn ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                        {!isOwn && <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px', marginLeft: '4px' }}>{c.user_name}</div>}
                        <div style={{ 
                          padding: '12px 16px', 
                          background: isOwn ? 'var(--primary)' : 'white', 
                          color: isOwn ? 'white' : 'var(--text-primary)',
                          borderRadius: '16px',
                          borderBottomRightRadius: isOwn ? '4px' : '16px',
                          borderBottomLeftRadius: !isOwn ? '4px' : '16px',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                          border: isOwn ? 'none' : '1px solid var(--border)'
                        }}>
                          <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.5 }}>{c.comment}</p>
                        </div>
                        <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '4px', textAlign: isOwn ? 'right' : 'left', alignSelf: isOwn ? 'flex-end' : 'flex-start' }}>
                          {new Date(c.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} • {new Date(c.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="drawer-footer" style={{ padding: '16px 24px', background: 'white', borderTop: '1px solid var(--border)' }}>
              <textarea 
                className="form-input" 
                rows="2" 
                placeholder="Type a reply..."
                value={newComment}
                onChange={e => {
                  const val = e.target.value;
                  setNewComment(val);
                  if (selectedEntryForComments) {
                    localStorage.setItem(`comment_draft_${selectedEntryForComments.id}`, val);
                  }
                }}
                style={{ width: '100%', resize: 'none', marginBottom: '12px', background: '#f8fafc' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button className="btn btn-secondary" onClick={() => setShowCommentsModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={handleAddComment} disabled={!newComment.trim()}>
                  Post Reply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Timesheets;
